import { cn } from '@/lib/utils';

/**
 * Cupertino-style toggle switch. Pure styled button — no dependency on a
 * primitives library (the rest of this folder uses @base-ui/react but its
 * Switch primitive isn't necessary for this surface area).
 *
 * Accessibility contract (PHI-adjacent UI must be screen-reader navigable
 * per @samer review on PR #64 — see `switch.test.tsx` for pinned cases):
 *   - `role="switch"` exposes correct semantic role.
 *   - `aria-checked` reflects state; required prop ensures consumers can't
 *     skip the announcement of what the toggle controls.
 *   - `aria-label` REQUIRED — no visible label on the switch itself, so
 *     screen readers depend on it. Pass the same string the visible label
 *     uses next to the switch.
 *   - Native `<button>` element gives Space + Enter activation for free.
 *   - Visible focus ring on `focus-visible` (keyboard focus only, not mouse
 *     clicks) — 2px brand-primary ring with offset.
 *
 * Visual: on = green pill (status-success), off = grey pill, animated thumb.
 */
export interface SwitchProps {
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: (next: boolean) => void;
  /** REQUIRED for accessibility — see component docs. */
  'aria-label': string;
}

export function Switch({
  checked,
  disabled = false,
  onCheckedChange,
  'aria-label': ariaLabel,
}: SwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className={cn(
        'relative inline-flex h-8 w-12 items-center rounded-full transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
        'focus-visible:ring-brand-primary',
        checked ? 'bg-status-success' : 'bg-grey-300',
        disabled && 'cursor-not-allowed opacity-50',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'inline-block h-6 w-6 rounded-full bg-white shadow transition-transform',
          checked ? 'translate-x-5' : 'translate-x-0.5',
        )}
      />
    </button>
  );
}
